import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Book, Plus, Edit, Trash2, Upload, Download, Key, Database, Loader2, Search, X, ArrowUpDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { invalidateKnowledgeCache } from "@/lib/knowledge-base";
import type { KnowledgeEntry, InsertKnowledgeEntry } from "@shared/schema";

type FormEntry = Omit<InsertKnowledgeEntry, 'materials' | 'damageTypes' | 'repairMethods' | 'recommendations'> & {
  materialsText: string;
  damageTypesText: string;
  repairMethodsText: string;
  recommendationsText: string;
};

const defaultForm: FormEntry = {
  title: "",
  description: "",
  category: "materials",
  priority: "medium",
  materialsText: "",
  damageTypesText: "",
  repairMethodsText: "",
  recommendationsText: "",
  costMin: 0,
  costMax: 0,
  costUnit: "EUR/m²",
};

function entryToForm(entry: KnowledgeEntry): FormEntry {
  return {
    title: entry.title,
    description: entry.description,
    category: entry.category,
    priority: entry.priority,
    materialsText: entry.materials.join(", "),
    damageTypesText: entry.damageTypes.join(", "),
    repairMethodsText: entry.repairMethods.join(", "),
    recommendationsText: entry.recommendations.join(", "),
    costMin: entry.costMin,
    costMax: entry.costMax,
    costUnit: entry.costUnit,
  };
}

function formToPayload(form: FormEntry): InsertKnowledgeEntry {
  return {
    title: form.title,
    description: form.description,
    category: form.category,
    priority: form.priority,
    materials: form.materialsText.split(",").map(s => s.trim()).filter(Boolean),
    damageTypes: form.damageTypesText.split(",").map(s => s.trim()).filter(Boolean),
    repairMethods: form.repairMethodsText.split(",").map(s => s.trim()).filter(Boolean),
    recommendations: form.recommendationsText.split(",").map(s => s.trim()).filter(Boolean),
    costMin: Number(form.costMin),
    costMax: Number(form.costMax),
    costUnit: form.costUnit,
  };
}

const LS_SEARCH_KEY = 'kb_searchQuery';
const LS_CATEGORY_KEY = 'kb_filterCategory';
const LS_PRIORITY_KEY = 'kb_filterPriority';
const LS_SORT_KEY = 'kb_sortBy';
const LS_TAB_KEY = 'kb_selectedTab';

const PRIORITY_ORDER: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export function KnowledgeBaseManager() {
  const [editingEntry, setEditingEntry] = useState<KnowledgeEntry | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteEntryId, setDeleteEntryId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState(() => {
    const VALID_TABS = ['entries', 'settings', 'data'];
    const stored = localStorage.getItem(LS_TAB_KEY);
    return stored && VALID_TABS.includes(stored) ? stored : 'entries';
  });
  const [addForm, setAddForm] = useState<FormEntry>(defaultForm);
  const [editForm, setEditForm] = useState<FormEntry>(defaultForm);
  const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem(LS_SEARCH_KEY) || '');
  const [filterCategory, setFilterCategory] = useState(() => localStorage.getItem(LS_CATEGORY_KEY) || 'all');
  const [filterPriority, setFilterPriority] = useState(() => localStorage.getItem(LS_PRIORITY_KEY) || 'all');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(LS_SORT_KEY) || 'default');
  const { toast } = useToast();

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    localStorage.setItem(LS_SEARCH_KEY, value);
  };

  const handleCategoryChange = (value: string) => {
    setFilterCategory(value);
    localStorage.setItem(LS_CATEGORY_KEY, value);
  };

  const handlePriorityChange = (value: string) => {
    setFilterPriority(value);
    localStorage.setItem(LS_PRIORITY_KEY, value);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    localStorage.setItem(LS_SORT_KEY, value);
  };

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    localStorage.setItem(LS_TAB_KEY, value);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterCategory('all');
    setFilterPriority('all');
    setSortBy('default');
    localStorage.removeItem(LS_SEARCH_KEY);
    localStorage.removeItem(LS_CATEGORY_KEY);
    localStorage.removeItem(LS_PRIORITY_KEY);
    localStorage.removeItem(LS_SORT_KEY);
  };

  const { data: entries = [], isLoading } = useQuery<KnowledgeEntry[]>({
    queryKey: ['/api/knowledge'],
  });

  const filteredEntries = entries
    .filter((entry) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || (
        entry.title.toLowerCase().includes(q) ||
        entry.description.toLowerCase().includes(q) ||
        entry.category.toLowerCase().includes(q) ||
        entry.materials.some(m => m.toLowerCase().includes(q)) ||
        entry.damageTypes.some(d => d.toLowerCase().includes(q)) ||
        entry.repairMethods.some(r => r.toLowerCase().includes(q)) ||
        entry.recommendations.some(r => r.toLowerCase().includes(q))
      );
      const matchesCategory = filterCategory === 'all' || entry.category === filterCategory;
      const matchesPriority = filterPriority === 'all' || entry.priority === filterPriority;
      return matchesSearch && matchesCategory && matchesPriority;
    })
    .sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title, 'de');
      if (sortBy === 'priority') return (PRIORITY_ORDER[b.priority] ?? 0) - (PRIORITY_ORDER[a.priority] ?? 0);
      if (sortBy === 'category') return a.category.localeCompare(b.category, 'de');
      return 0;
    });

  // Hofer Seeboden (Fassade · Putz · Sanierung) – Kategorien spiegeln die
  // Default-Wissenseinträge aus `server/storage.ts` (DEFAULT_KNOWLEDGE_ENTRIES).
  const categories = [
    { value: "Fassadenreinigung", label: "Fassadenreinigung" },
    { value: "Putzsanierung", label: "Putzsanierung" },
    { value: "WDVS", label: "Wärmedämm-Verbundsystem (WDVS)" },
    { value: "Sockelsanierung", label: "Sockelsanierung" },
    { value: "Betonsanierung", label: "Beton-Sanierung" },
    { value: "Risssanierung", label: "Risssanierung" },
    { value: "Fassadenanstrich", label: "Fassadenanstrich & Beschichtung" },
    { value: "materials", label: "Materialien" },
    { value: "damage", label: "Schäden" },
    { value: "repair", label: "Reparaturen" },
    { value: "maintenance", label: "Wartung" },
    { value: "safety", label: "Sicherheit" },
  ];

  const priorities = [
    { value: "low", label: "Niedrig" },
    { value: "medium", label: "Mittel" },
    { value: "high", label: "Hoch" },
    { value: "urgent", label: "Dringend" },
  ];

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ['/api/knowledge'] });
    invalidateKnowledgeCache();
  }

  const createMutation = useMutation({
    mutationFn: (data: InsertKnowledgeEntry) =>
      apiRequest("POST", "/api/knowledge", data),
    onSuccess: () => {
      invalidateAll();
      setAddForm(defaultForm);
      setIsAddDialogOpen(false);
      toast({ title: "Eintrag hinzugefügt", description: "Der Wissensdatenbank-Eintrag wurde erfolgreich hinzugefügt." });
    },
    onError: () => {
      toast({ title: "Fehler", description: "Eintrag konnte nicht gespeichert werden.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: InsertKnowledgeEntry }) =>
      apiRequest("PUT", `/api/knowledge/${id}`, data),
    onSuccess: () => {
      invalidateAll();
      setEditingEntry(null);
      toast({ title: "Eintrag aktualisiert", description: "Der Wissensdatenbank-Eintrag wurde erfolgreich aktualisiert." });
    },
    onError: () => {
      toast({ title: "Fehler", description: "Eintrag konnte nicht aktualisiert werden.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/knowledge/${id}`),
    onSuccess: () => {
      invalidateAll();
      setDeleteEntryId(null);
      toast({ title: "Eintrag gelöscht", description: "Der Wissensdatenbank-Eintrag wurde entfernt." });
    },
    onError: () => {
      toast({ title: "Fehler", description: "Eintrag konnte nicht gelöscht werden.", variant: "destructive" });
    },
  });

  const handleAddEntry = () => {
    if (!addForm.title || !addForm.description) {
      toast({ title: "Fehler", description: "Titel und Beschreibung sind erforderlich.", variant: "destructive" });
      return;
    }
    createMutation.mutate(formToPayload(addForm));
  };

  const handleOpenEdit = (entry: KnowledgeEntry) => {
    setEditingEntry(entry);
    setEditForm(entryToForm(entry));
  };

  const handleSaveEdit = () => {
    if (!editingEntry) return;
    if (!editForm.title || !editForm.description) {
      toast({ title: "Fehler", description: "Titel und Beschreibung sind erforderlich.", variant: "destructive" });
      return;
    }
    updateMutation.mutate({ id: editingEntry.id, data: formToPayload(editForm) });
  };

  const handleExportData = () => {
    const data = {
      knowledgeBase: entries,
      exportDate: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bauwerk-wissensdatenbank-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Daten exportiert", description: "Die Wissensdatenbank wurde erfolgreich exportiert." });
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        const importEntries: InsertKnowledgeEntry[] = (data.knowledgeBase || []).map((item: any) => ({
          title: item.title || "",
          description: item.description || "",
          category: item.category || "materials",
          priority: item.priority || "medium",
          materials: Array.isArray(item.materials) ? item.materials : [],
          damageTypes: Array.isArray(item.damageTypes) ? item.damageTypes : [],
          repairMethods: Array.isArray(item.repairMethods) ? item.repairMethods : [],
          recommendations: Array.isArray(item.recommendations) ? item.recommendations : [],
          costMin: item.costMin ?? item.estimatedCost?.min ?? 0,
          costMax: item.costMax ?? item.estimatedCost?.max ?? 0,
          costUnit: item.costUnit ?? item.estimatedCost?.unit ?? "EUR/m²",
        }));
        for (const entry of importEntries) {
          await apiRequest("POST", "/api/knowledge", entry);
        }
        invalidateAll();
        toast({ title: "Daten importiert", description: `${importEntries.length} Einträge wurden erfolgreich importiert.` });
      } catch {
        toast({ title: "Import fehlgeschlagen", description: "Die Datei konnte nicht verarbeitet werden.", variant: "destructive" });
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const EntryForm = ({ form, setForm }: { form: FormEntry; setForm: (f: FormEntry) => void }) => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">Titel</Label>
        <Input
          id="title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="z.B. Betonreparatur bei Frostschäden"
        />
      </div>
      <div>
        <Label htmlFor="description">Beschreibung</Label>
        <Textarea
          id="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Detaillierte Beschreibung der Reparaturmethode..."
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="category">Kategorie</Label>
          <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="priority">Priorität</Label>
          <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorities.map(prio => (
                <SelectItem key={prio.value} value={prio.value}>{prio.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="materials">Materialien (durch Komma getrennt)</Label>
        <Input
          id="materials"
          value={form.materialsText}
          onChange={(e) => setForm({ ...form, materialsText: e.target.value })}
          placeholder="Beton, Stahl, Mauerwerk"
        />
      </div>
      <div>
        <Label htmlFor="damageTypes">Schadenstypen (durch Komma getrennt)</Label>
        <Input
          id="damageTypes"
          value={form.damageTypesText}
          onChange={(e) => setForm({ ...form, damageTypesText: e.target.value })}
          placeholder="Risse, Abplatzungen, Korrosion"
        />
      </div>
      <div>
        <Label htmlFor="repairMethods">Reparaturmethoden (durch Komma getrennt)</Label>
        <Input
          id="repairMethods"
          value={form.repairMethodsText}
          onChange={(e) => setForm({ ...form, repairMethodsText: e.target.value })}
          placeholder="Reprofilierung, Injektionsverfahren"
        />
      </div>
      <div>
        <Label htmlFor="recommendations">Empfehlungen (durch Komma getrennt)</Label>
        <Input
          id="recommendations"
          value={form.recommendationsText}
          onChange={(e) => setForm({ ...form, recommendationsText: e.target.value })}
          placeholder="pH-Wert prüfen, Feuchtigkeitsmessung durchführen"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="minCost">Min. Kosten</Label>
          <Input
            id="minCost"
            type="number"
            value={form.costMin}
            onChange={(e) => setForm({ ...form, costMin: Number(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="maxCost">Max. Kosten</Label>
          <Input
            id="maxCost"
            type="number"
            value={form.costMax}
            onChange={(e) => setForm({ ...form, costMax: Number(e.target.value) })}
          />
        </div>
        <div>
          <Label htmlFor="unit">Einheit</Label>
          <Select value={form.costUnit} onValueChange={(value) => setForm({ ...form, costUnit: value })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR/m²">EUR/m²</SelectItem>
              <SelectItem value="EUR/Stück">EUR/Stück</SelectItem>
              <SelectItem value="EUR/lfd.m">EUR/lfd.m</SelectItem>
              <SelectItem value="EUR/Std">EUR/Std</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-6 h-6 text-bauwerk-teal" />
          Wissensdatenbank-Verwaltung
        </CardTitle>
        <CardDescription>
          Verwalten Sie Fachwissen, API-Keys und Datenimport/-export
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Tabs value={selectedTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="entries">Einträge</TabsTrigger>
            <TabsTrigger value="settings">Einstellungen</TabsTrigger>
            <TabsTrigger value="data">Daten</TabsTrigger>
          </TabsList>

          <TabsContent value="entries" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Book className="w-5 h-5 text-bauwerk-teal" />
                <h3 className="text-lg font-semibold">Wissensdatenbank</h3>
                <Badge variant="outline">
                  {filteredEntries.length}{filteredEntries.length !== entries.length ? ` / ${entries.length}` : ""} Einträge
                </Badge>
              </div>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Eintrag hinzufügen
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Neuer Wissensdatenbank-Eintrag</DialogTitle>
                  </DialogHeader>
                  <EntryForm form={addForm} setForm={setAddForm} />
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={handleAddEntry} disabled={createMutation.isPending}>
                      {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Speichern
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Einträge durchsuchen (Titel, Beschreibung, Materialien, Schäden, Reparaturen...)..."
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Suche löschen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Select value={filterCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Alle Kategorien" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPriority} onValueChange={handlePriorityChange}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Alle Prioritäten" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Prioritäten</SelectItem>
                  {priorities.map(prio => (
                    <SelectItem key={prio.value} value={prio.value}>{prio.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={handleSortChange}>
                <SelectTrigger className="w-full sm:w-44">
                  <ArrowUpDown className="w-4 h-4 mr-1 shrink-0 text-muted-foreground" />
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standardreihenfolge</SelectItem>
                  <SelectItem value="title">Titel (A–Z)</SelectItem>
                  <SelectItem value="priority">Priorität (hoch–niedrig)</SelectItem>
                  <SelectItem value="category">Kategorie (A–Z)</SelectItem>
                </SelectContent>
              </Select>
              {(searchQuery || filterCategory !== 'all' || filterPriority !== 'all' || sortBy !== 'default') && (
                <Button variant="ghost" size="icon" onClick={handleClearFilters} title="Filter zurücksetzen">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-bauwerk-teal" />
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredEntries.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    {entries.length === 0
                      ? "Noch keine Einträge vorhanden."
                      : "Keine Einträge gefunden. Filter anpassen oder zurücksetzen."}
                  </div>
                )}
                {filteredEntries.map((entry) => (
                  <Card key={entry.id} className="border-l-4 border-l-bauwerk-teal">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-base">
                            <HighlightedText text={entry.title} query={searchQuery} />
                          </CardTitle>
                          <CardDescription>
                            <HighlightedText text={entry.description} query={searchQuery} />
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(entry)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteEntryId(entry.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline">{entry.category}</Badge>
                        <Badge variant={entry.priority === 'urgent' ? 'destructive' : 'secondary'}>
                          {entry.priority}
                        </Badge>
                        <Badge variant="secondary">
                          {entry.costMin}-{entry.costMax} {entry.costUnit}
                        </Badge>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        {entry.materials.length > 0 && (
                          <div>
                            <p className="font-medium mb-1">Materialien:</p>
                            <p className="text-gray-600">
                              {entry.materials.map((m, i) => (
                                <span key={i}>{i > 0 && ", "}<HighlightedText text={m} query={searchQuery} /></span>
                              ))}
                            </p>
                          </div>
                        )}
                        {entry.damageTypes.length > 0 && (
                          <div>
                            <p className="font-medium mb-1">Schadenstypen:</p>
                            <p className="text-gray-600">
                              {entry.damageTypes.map((d, i) => (
                                <span key={i}>{i > 0 && ", "}<HighlightedText text={d} query={searchQuery} /></span>
                              ))}
                            </p>
                          </div>
                        )}
                        {entry.repairMethods.length > 0 && (
                          <div>
                            <p className="font-medium mb-1">Reparaturmethoden:</p>
                            <p className="text-gray-600">
                              {entry.repairMethods.map((r, i) => (
                                <span key={i}>{i > 0 && ", "}<HighlightedText text={r} query={searchQuery} /></span>
                              ))}
                            </p>
                          </div>
                        )}
                        {entry.recommendations.length > 0 && (
                          <div>
                            <p className="font-medium mb-1">Empfehlungen:</p>
                            <p className="text-gray-600">
                              {entry.recommendations.map((r, i) => (
                                <span key={i}>{i > 0 && ", "}<HighlightedText text={r} query={searchQuery} /></span>
                              ))}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  KI-Einstellungen
                </CardTitle>
                <CardDescription>
                  API-Keys für Anthropic (Claude) und OpenAI werden zentral verwaltet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Die KI-API-Keys (Anthropic und OpenAI) werden nun zentral in den
                  KI-Einstellungen verwaltet, die über das Benutzermenü (oben rechts) zugänglich sind.
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.dispatchEvent(new CustomEvent("open-api-key-settings"))}
                  className="flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  KI-Einstellungen öffnen
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="data" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Datenmanagement
                </CardTitle>
                <CardDescription>
                  Importieren und exportieren Sie Wissensdatenbank-Einträge
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <Button onClick={handleExportData} className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Daten exportieren
                  </Button>

                  <div className="relative">
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportData}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      id="import-file"
                    />
                    <Button asChild className="flex items-center gap-2">
                      <label htmlFor="import-file" className="cursor-pointer">
                        <Upload className="w-4 h-4" />
                        Daten importieren
                      </label>
                    </Button>
                  </div>
                </div>

                <div className="p-3 bg-yellow-50 rounded text-sm">
                  <p className="font-medium mb-1">Hinweis:</p>
                  <p className="text-gray-700">
                    Beim Import werden neue Einträge zur bestehenden Wissensdatenbank hinzugefügt.
                    Exportieren Sie vorher Ihre Daten als Backup.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Bearbeiten-Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Eintrag bearbeiten</DialogTitle>
          </DialogHeader>
          <EntryForm form={editForm} setForm={setEditForm} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditingEntry(null)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lösch-Dialog */}
      <AlertDialog open={!!deleteEntryId} onOpenChange={() => setDeleteEntryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eintrag löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diesen Wissensdatenbank-Eintrag löschen möchten?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntryId && deleteMutation.mutate(deleteEntryId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
